import { useMemo, useRef, useState } from "react";
import {
  AR_INTERACT_LIMITS,
  AR_LAYER_MIN,
  arInteractIssues,
  arLayerImgKey,
  arLayerModelKey,
  arTargetKey,
  isArLayerId,
  normalizeArInteract,
  type ArHotspot,
  type ArInteract,
  type ArLayerDef,
  type AssetSpec,
  MAGIC_SFX,
  MAGIC_MOTION,
  MAGIC_ENTER,
  MAGIC_ENTER_DELAY_MAX,
  MAGIC_MOTION_PHASE_MAX,
  type MagicEnter,
  type MagicMotion,
  type MagicSfx,
  repoArtOffer,
  MODEL_WARN_KB,
  classLoadSeconds,
} from "@greg-chick/shared";
import { absArtUrl, uploadAsset, type ManifestEntry } from "../lib/contentApi";
import { useRepoAssets } from "../lib/repoAssets";
import { useKb } from "../lib/useKb";
import { magicSfxLike, playMagicSfx } from "../lib/magicSound";
import { SlotUploader } from "./SlotUploader";
import { Model3dMini } from "./Model3dMini";
import { ModelFilePicker } from "./ModelFilePicker";
import { PageMagicPreview } from "./PageMagicPreview";

/**
 * 🪄 **「마법 꾸미기」 — 운영자가 층·누르는 자리·손잡이를 만든다** (docs/build/35, FR-AR-09).
 *
 * ## 어디에 저장되나
 * 정의는 **타깃 항목의 `interact`**(`fallbackYoutube` 와 같은 자리), 그림은
 * `ar.layer.img.<층>.<장면>`. 새 표·새 API 가 없다.
 *
 * ## 🔴 판정을 여기서 다시 짜지 않는다
 * 「무엇이 보이나·누르면 무엇이 되나」는 미리보기(`PageMagicPreview`)가 **아이 화면과 같은
 * shared 함수**로 판정한다(`previewParity.test.ts` 가 강제). 여기는 **정의를 고치는 일**만 한다.
 * 반만 만든 것은 `arInteractIssues` 가 말로 알려 준다 — 그 판정도 shared 한 곳이라
 * [✅ 출시 준비]와 **같은 말**을 한다.
 *
 * ## 🔴 그림이 없으면 아이 화면에 안 뜬다
 * 정의만 저장하면 「✨ 마법 보기」가 안 나온다(`bookData` 가 그렇게만 채운다).
 * 그래서 층마다 **올리는 자리**를 바로 옆에 둔다 — 「저장은 했는데 왜 안 보이지」를 막는다.
 */
const LAYER_SPEC: AssetSpec = { formats: ["webp", "png"], alphaRequired: true, maxLongEdgePx: 4096 };

interface Props {
  idKey: string; // <책>.<쪽> 또는 경주 카드 id(`ar_sumaksae`)
  title: string;
  pageImageUrl?: string;
  /**
   * 🔴 **아이 화면과 같은 쪽 비율**(CSS `aspect-ratio`, 예 `"1/1"`).
   *
   * 자리(층·누르는 자리)는 이 상자 기준 0~1 이다. 여기만 3:4 로 고정해 두면 정사각 쪽에서
   * 위아래에 빈 띠가 생기고, 운영자가 금관 위에 정확히 그린 네모가 **아이 화면에선 딴 데**로
   * 간다 — 아무 에러 없이. 그래서 비율을 넘겨받는다.
   */
  pageAspect?: string;
  assets: Record<string, ManifestEntry>;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
}

type Tab = "layers" | "hotspots" | "slider";

/** 새 id — 사람이 안 짓게(층 id 는 자리 이름에 들어가서 규칙이 있다). */
function nextId(prefix: string, taken: Set<string>): string {
  for (let i = 1; i < 100; i++) {
    const id = `${prefix}${i}`;
    if (!taken.has(id)) return id;
  }
  return `${prefix}${Date.now() % 1000}`;
}

export function ArMagicEditor({ idKey, title, pageImageUrl, pageAspect, assets, setEntry }: Props) {
  /** 📦 저장소가 아는 그림표 — 층 옆 「저장소 그림으로 바꾸기」에 쓴다(못 읽으면 그 단추만 안 뜬다). */
  const repoAssets = useRepoAssets();
  const targetKey = arTargetKey(idKey);
  const target = assets[targetKey];
  const [tab, setTab] = useState<Tab>("layers");
  /**
   * 지금 그림 위에서 네모를 그리고 있는 대상.
   * `hot` = 누르는 자리, `layer` = **층 그림을 놓을 자리**(둘 다 같은 끌기로 그린다).
   */
  const [picking, setPicking] = useState<{ kind: "hot" | "layer"; id: string } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  /** 저장된 정의(원본 그대로 — 정규화는 아이 화면이 한다). 없으면 빈 것. */
  const def: ArInteract = useMemo(
    () => (target?.interact as ArInteract | undefined) ?? { layers: [], hotspots: [] },
    [target],
  );
  const issues = useMemo(() => (def.layers.length ? arInteractIssues(def) : []), [def]);
  const shown = useMemo(() => normalizeArInteract(def), [def]);

  /** 층 그림 — 올라온 것만 미리보기에 넘긴다(아이 화면과 같은 규칙). */
  const layerImgs = useMemo(() => {
    const out: Record<string, { url: string; fps?: number; once?: boolean }> = {};
    for (const l of def.layers) {
      const e = assets[arLayerImgKey(idKey, l.id)];
      if (e?.url) out[l.id] = { url: e.url, ...(typeof e.fps === "number" ? { fps: e.fps } : {}), ...((e as { once?: unknown }).once === true ? { once: true } : {}) };
    }
    return out;
  }, [def, assets, idKey]);
  /**
   * 🧊 **3D 파일** — 층에 올라온 .glb 를 미리보기에 넘긴다. 미리보기가 아이 화면과 같은 무대로 세운다(2026-09-13).
   * 🔴 여는 주소(절대형)로 — 저장소 3D(`/art/…`)를 관리자 출처에 붙이면 관리자 index.html 을 받는다.
   */
  const layerModels = useMemo(() => {
    const out: Record<string, string> = {};
    for (const l of def.layers) {
      const u = absArtUrl(assets[arLayerModelKey(idKey, l.id)]?.url);
      if (u) out[l.id] = u;
    }
    return out;
  }, [def, assets, idKey]);

  function save(next: ArInteract) {
    if (!target) return; // 타깃(카메라가 외운 그림)이 있어야 얹을 자리가 있다
    const empty = next.layers.length === 0;
    setEntry(targetKey, { ...target, interact: empty ? undefined : next });
  }

  const layerIds = new Set(def.layers.map((l) => l.id));

  function addLayer() {
    const id = nextId("l", layerIds);
    save({ ...def, layers: [...def.layers, { id, label_ko: `층 ${def.layers.length + 1}`, startVisible: def.layers.length === 0 }] });
  }
  function patchLayer(id: string, fn: (l: ArLayerDef) => ArLayerDef) {
    save({ ...def, layers: def.layers.map((l) => (l.id === id ? fn(l) : l)) });
  }
  function removeLayer(id: string) {
    setEntry(arLayerImgKey(idKey, id), null); // 그림도 같이 — 안 지우면 R2 에 남는다
    setEntry(arLayerModelKey(idKey, id), null); // 3D 파일도
    save({
      ...def,
      layers: def.layers.filter((l) => l.id !== id),
      hotspots: def.hotspots.filter((h) => h.layer !== id),
      ...(def.slider && (def.slider.a === id || def.slider.b === id) ? { slider: undefined } : {}),
    });
  }

  function addHotspot() {
    const layer = def.layers[0]?.id;
    if (!layer) return;
    const id = nextId("h", new Set(def.hotspots.map((h) => h.id)));
    const next: ArHotspot = {
      id,
      label_ko: `누르는 자리 ${def.hotspots.length + 1}`,
      layer,
      rect: { x: 0.4, y: 0.4, w: 0.2, h: 0.2 },
      action: { kind: "show", on: [], off: [] },
    };
    save({ ...def, hotspots: [...def.hotspots, next] });
    setPicking({ kind: "hot", id });
  }
  function patchHot(id: string, fn: (h: ArHotspot) => ArHotspot) {
    save({ ...def, hotspots: def.hotspots.map((h) => (h.id === id ? fn(h) : h)) });
  }

  /**
   * 쪽 그림 위에서 끌어 네모 그리기 — 좌표를 숫자로 칠 일이 없게.
   *
   * 🔴 누르는 자리와 **층 그림 자리**가 같은 끌기를 쓴다. 층에 자리가 없으면 운영자는
   * 금관 한 장을 쪽 크기에 미리 맞춰 놓은 큰 판으로 **다시 만들어야** 한다.
   */
  function onStagePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!picking) return;
    const aim = picking;
    const box = stageRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    const min = aim.kind === "hot" ? AR_INTERACT_LIMITS.minHit : AR_LAYER_MIN;
    const sx = (e.clientX - box.left) / box.width;
    const sy = (e.clientY - box.top) / box.height;
    e.currentTarget.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      const cx = (ev.clientX - box.left) / box.width;
      const cy = (ev.clientY - box.top) / box.height;
      const x = Math.max(0, Math.min(sx, cx));
      const y = Math.max(0, Math.min(sy, cy));
      const w = Math.min(1 - x, Math.max(min, Math.abs(cx - sx)));
      const h = Math.min(1 - y, Math.max(min, Math.abs(cy - sy)));
      if (aim.kind === "hot") patchHot(aim.id, (hs) => ({ ...hs, rect: { x, y, w, h } }));
      else patchLayer(aim.id, (l) => ({ ...l, rect: { x, y, w, h } }));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setPicking(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  if (!target) {
    return (
      <p className="zone__tip">
        먼저 위에서 <b>카메라가 이 그림을 외우게</b> 해 주세요. 그래야 마법을 얹을 수 있어요.
      </p>
    );
  }

  return (
    <div className="magicedit">
      <div className="magicedit__head">
        <b>🪄 마법 꾸미기</b>
        <span className="zone__tip">누르면 그림이 바뀌게 만들어요</span>
      </div>

      <div className="magicedit__tabs" role="tablist" aria-label="마법 꾸미기 단계">
        {([
          ["layers", `① 층 ${def.layers.length}`],
          ["hotspots", `② 누르는 자리 ${def.hotspots.length}`],
          ["slider", def.slider ? "③ 손잡이 ✓" : "③ 손잡이"],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? "on" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="magicedit__body">
        <div className="magicedit__panel">
          {tab === "layers" ? (
            <>
              {def.layers.map((l) => (
                <div key={l.id} className="magicedit__layer">
                  <div className="magicedit__row">
                    <input
                      value={l.label_ko}
                      aria-label={`층 이름 — ${l.id}`}
                      maxLength={AR_INTERACT_LIMITS.label}
                      onChange={(e) => patchLayer(l.id, (x) => ({ ...x, label_ko: e.target.value }))}
                    />
                    <label className="bk-check">
                      <input
                        type="checkbox"
                        checked={l.startVisible}
                        onChange={(e) => patchLayer(l.id, (x) => ({ ...x, startVisible: e.target.checked }))}
                      />
                      처음부터 보임
                    </label>
                    {/* 🍃 **한 장으로 움직이기** — 그림은 그대로 두고 화면이 흔든다.
                        모양이 바뀌는 애니(꼬투리 열림·빵 한 입)는 여기 말고 **띠**(여러 장 이어 붙인 그림)로 만든다. */}
                    <label>
                      움직임
                      <select
                        value={l.motion ?? ""}
                        onChange={(e) =>
                          patchLayer(l.id, ({ motion: _m, motionPhase: _p, ...x }) =>
                            e.target.value ? { ...x, motion: e.target.value as MagicMotion, ...(_p ? { motionPhase: _p } : {}) } : x,
                          )
                        }
                      >
                        <option value="">가만히</option>
                        {MAGIC_MOTION.map((mo) => (
                          <option key={mo.id} value={mo.id}>{mo.label_ko}</option>
                        ))}
                      </select>
                      {/* 🌾 엇박 — 같은 움직임의 여러 겹을 조금씩 다르게 두면 바람이 **건너오는** 물결이 된다(shared `layerMotionVars`) */}
                      {l.motion ? (
                        <span title="여러 겹이 같은 움직임이면 조금씩 다르게 두세요 — 바람이 밭을 건너오는 것처럼 보여요.">
                          {" "}엇박
                          <input
                            type="number"
                            min={0}
                            max={MAGIC_MOTION_PHASE_MAX}
                            step={100}
                            value={l.motionPhase ?? 0}
                            aria-label={`${l.label_ko} 움직임 엇박(ms)`}
                            onChange={(e) => {
                              const ms = Math.max(0, Math.min(MAGIC_MOTION_PHASE_MAX, Math.round(Number(e.target.value) || 0)));
                              patchLayer(l.id, ({ motionPhase: _p, ...x }) => (ms > 0 ? { ...x, motionPhase: ms } : x));
                            }}
                          />
                          ms
                        </span>
                      ) : null}
                    </label>
                    {/* 🔗 다른 층을 따라 보인다 — 「밤이 되면 별이 반짝」처럼 손잡이로 섞이는 층을 따라갈 때(shared `layerOpacity`) */}
                    <label className="bk-field bk-field--inline" title="고른 층이 보이는 만큼만 이 층도 보여요. 손잡이로 섞이는 층을 따라갈 때 써요. 한 단계만 돼요.">
                      따라갈 층
                      <select
                        value={l.follow ?? ""}
                        onChange={(e) =>
                          patchLayer(l.id, ({ follow: _f, ...x }) => (e.target.value ? { ...x, follow: e.target.value } : x))
                        }
                      >
                        <option value="">없음</option>
                        {def.layers
                          .filter((o) => o.id !== l.id && !o.follow)
                          .map((o) => (
                            <option key={o.id} value={o.id}>{o.label_ko}</option>
                          ))}
                      </select>
                    </label>
                    {/* ✨ 등장 연출 — 켜지는 순간 한 번. 처음부터 보이는 층에는 안 붙는다(shared `layerEnter`) */}
                    {!l.startVisible ? (
                      <label className="bk-field bk-field--inline" title="이 층이 켜지는 순간 한 번 움직여요. 「늦춤」으로 여러 층을 차례로 놓을 수 있어요.">
                        등장
                        <select
                          value={l.enter ?? ""}
                          onChange={(e) =>
                            patchLayer(l.id, ({ enter: _e, enterDelay: _d, ...x }) =>
                              e.target.value ? { ...x, enter: e.target.value as MagicEnter, ...(_d ? { enterDelay: _d } : {}) } : x,
                            )
                          }
                        >
                          <option value="">바로</option>
                          {MAGIC_ENTER.map((en) => (
                            <option key={en.id} value={en.id}>{en.label_ko}</option>
                          ))}
                        </select>
                        {l.enter ? (
                          <>
                            늦춤
                            <input
                              type="number"
                              min={0}
                              max={MAGIC_ENTER_DELAY_MAX}
                              step={50}
                              value={l.enterDelay ?? 0}
                              aria-label={`${l.label_ko} 등장 늦춤(ms)`}
                              onChange={(e) => {
                                const ms = Math.max(0, Math.min(MAGIC_ENTER_DELAY_MAX, Math.round(Number(e.target.value) || 0)));
                                patchLayer(l.id, ({ enterDelay: _d, ...x }) => (ms > 0 ? { ...x, enterDelay: ms } : x));
                              }}
                            />
                            ms
                          </>
                        ) : null}
                      </label>
                    ) : null}
                    {/* 🖼️ 새 화면 — 쪽 위에 작게 겹치지 않고 **쪽을 덮고 크게**(전시관·보리밭). 판정은 shared `full`.
                        이 층의 자리는 **새 화면 기준**이라, 켜면 미리보기가 새 화면으로 바뀌고 거기서 끌어 잡는다. */}
                    <label
                      className="bk-check"
                      title="누르면 쪽 위가 아니라 새 화면으로 넘어가 크게 보여요(전시관·보리밭). 이 층의 자리는 새 화면 기준이에요."
                    >
                      <input
                        type="checkbox"
                        checked={!!l.full}
                        onChange={(e) =>
                          patchLayer(l.id, ({ full: _f, ...x }) => (e.target.checked ? { ...x, full: true } : x))
                        }
                      />
                      🖼️ 새 화면으로 열기
                    </label>
                    {/* 🔄 끌면 돌아가요 — 띠의 장이 각도(3D 를 돌려 찍은 것). 새 화면에서만 뜻이 있다(arInteractIssues 가 말해 준다). */}
                    {l.full ? (
                      <label className="bk-field bk-field--inline" title="이 층의 그림이 각도마다 찍은 띠(3D 를 돌려 찍은 것)일 때 고르세요.">
                        🔄 돌리기
                        <select
                          aria-label={`${l.label_ko} 돌리기`}
                          value={l.spin ? (l.spinAuto ? "auto" : "drag") : ""}
                          onChange={(e) =>
                            patchLayer(l.id, ({ spin: _s, spinAuto: _a, ...x }) =>
                              e.target.value === "drag" ? { ...x, spin: true } : e.target.value === "auto" ? { ...x, spin: true, spinAuto: true } : x,
                            )
                          }
                        >
                          <option value="">안 돌아요</option>
                          <option value="drag">아이가 끌어서 돌려요</option>
                          <option value="auto">저절로 천천히 돌다가, 끌면 손을 따라요</option>
                        </select>
                      </label>
                    ) : null}
                    <button type="button" className="slot__remove" onClick={() => removeLayer(l.id)} aria-label={`${l.label_ko} 층 지우기`}>
                      지우기
                    </button>
                  </div>
                  {/* 🔴 그림 한 장을 그대로 올리고 **자리를 여기서 잡는다** — 쪽 크기에 맞춰
                      다시 만들어 오라고 하면 운영자는 안 한다(memory: 다시 올리라면 안 한다). */}
                  <div className="magicedit__row">
                    <button
                      type="button"
                      className={`btn btn--sm${picking?.kind === "layer" && picking.id === l.id ? " on" : ""}`}
                      onClick={() => setPicking(picking?.kind === "layer" && picking.id === l.id ? null : { kind: "layer", id: l.id })}
                    >
                      {picking?.kind === "layer" && picking.id === l.id ? "그리는 중… 그림 위에서 끌어요" : "📍 그림 놓을 자리"}
                    </button>
                    <span className="zone__tip">
                      {l.rect
                        ? `가로 ${Math.round(l.rect.w * 100)}% · 세로 ${Math.round(l.rect.h * 100)}% 자리`
                        : "쪽 전체에 그려요"}
                    </span>
                    {l.rect ? (
                      <button
                        type="button"
                        className="slot__remove"
                        onClick={() => patchLayer(l.id, ({ rect: _drop, ...rest }) => rest)}
                        aria-label={`${l.label_ko} 자리 없애기`}
                      >
                        쪽 전체로
                      </button>
                    ) : null}
                  </div>
                  <SlotUploader
                    slotKey={arLayerImgKey(idKey, l.id)}
                    label={`🖼️ ${l.label_ko} 그림`}
                    spec={LAYER_SPEC}
                    entry={assets[arLayerImgKey(idKey, l.id)]}
                    onChange={setEntry}
                    alt={`${title} — ${l.label_ko}`}
                  />
                  {l.full ? (
                    <Layer3dCard
                      idKey={idKey}
                      layer={l}
                      model={assets[arLayerModelKey(idKey, l.id)]}
                      strip={assets[arLayerImgKey(idKey, l.id)]}
                      setEntry={setEntry}
                      onCredit={(v) => patchLayer(l.id, ({ credit_ko: _c, ...x }) => (v.trim() ? { ...x, credit_ko: v } : x))}
                    />
                  ) : null}
                  <LayerArtNote
                    entry={assets[arLayerImgKey(idKey, l.id)]}
                    repo={repoAssets?.[arLayerImgKey(idKey, l.id)] as ManifestEntry | undefined}
                    // 관리자 초안은 **여는 주소**(절대형)로 든다 — 상대형을 넣으면 미리보기가 깨진다. 게시할 때 되돌린다.
                    onUseRepo={(next) => setEntry(arLayerImgKey(idKey, l.id), { ...next, url: absArtUrl(next.url) as string })}
                  />
                </div>
              ))}
              {def.layers.length >= AR_INTERACT_LIMITS.layers ? (
                <p className="hint">층을 {AR_INTERACT_LIMITS.layers}장까지 만들 수 있어요.</p>
              ) : (
                <button type="button" className="btn btn--sm" onClick={addLayer}>
                  ＋ 층 더하기
                </button>
              )}
            </>
          ) : null}

          {tab === "hotspots" ? (
            <>
              {def.layers.length === 0 ? (
                <p className="hint">먼저 ① 층에서 그림을 한 장 올려 주세요.</p>
              ) : null}
              {def.hotspots.map((h) => (
                <div key={h.id} className="magicedit__hot">
                  <div className="magicedit__row">
                    <input
                      value={h.label_ko}
                      aria-label={`자리 이름 — ${h.id}`}
                      maxLength={AR_INTERACT_LIMITS.label}
                      onChange={(e) => patchHot(h.id, (x) => ({ ...x, label_ko: e.target.value }))}
                    />
                    <button
                      type="button"
                      className={`btn btn--sm${picking?.kind === "hot" && picking.id === h.id ? " on" : ""}`}
                      onClick={() => setPicking(picking?.kind === "hot" && picking.id === h.id ? null : { kind: "hot", id: h.id })}
                    >
                      {picking?.kind === "hot" && picking.id === h.id ? "그리는 중… 그림 위에서 끌어요" : "✏️ 자리 그리기"}
                    </button>
                    <button
                      type="button"
                      className="slot__remove"
                      onClick={() => save({ ...def, hotspots: def.hotspots.filter((x) => x.id !== h.id) })}
                      aria-label={`${h.label_ko} 자리 지우기`}
                    >
                      지우기
                    </button>
                  </div>
                  <div className="magicedit__row">
                    <label>
                      어느 층에
                      <select value={h.layer} onChange={(e) => patchHot(h.id, (x) => ({ ...x, layer: e.target.value }))}>
                        {def.layers.map((l) => (
                          <option key={l.id} value={l.id}>{l.label_ko}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      무엇을 할까
                      <select
                        value={h.action.kind}
                        onChange={(e) =>
                          patchHot(h.id, (x) => ({
                            ...x,
                            action: e.target.value === "say" ? { kind: "say", child_ko: "" } : { kind: "show", on: [], off: [] },
                          }))
                        }
                      >
                        <option value="show">보여주기</option>
                        <option value="say">말하기</option>
                      </select>
                    </label>
                    {/* 🔊 **소리** — 아이가 누른 그 순간에 난다. 소리 파일을 아직 안 올렸어도
                        그 자리에서 만든 소리가 나므로, 고르면 **바로** 들린다(눌러서 들어 보기). */}
                    <label>
                      🔊 소리
                      <select
                        value={h.action.sfx ?? ""}
                        onChange={(e) =>
                          patchHot(h.id, (x) => {
                            const { sfx: _drop, ...rest } = x.action;
                            const v = e.target.value;
                            if (v) playMagicSfx(v); // 고르는 순간 들려준다 — 이름만 보고는 못 고른다
                            return { ...x, action: v ? { ...rest, sfx: v as MagicSfx } : rest };
                          })
                        }
                      >
                        <option value="">소리 없음</option>
                        {MAGIC_SFX.map((sx) => (
                          <option key={sx.id} value={sx.id}>{sx.label_ko}</option>
                        ))}
                      </select>
                    </label>
                    {h.action.sfx ? (
                      <button
                        type="button"
                        className="btn btn--sm"
                        title={magicSfxLike(h.action.sfx)}
                        onClick={() => playMagicSfx(h.action.sfx)}
                      >
                        ▶️ 들어 보기
                      </button>
                    ) : null}
                  </div>

                  {h.action.kind === "show" ? (
                    <div className="magicedit__pick">
                      {def.layers.map((l) => {
                        const on = h.action.kind === "show" && h.action.on.includes(l.id);
                        const off = h.action.kind === "show" && h.action.off.includes(l.id);
                        return (
                          <div key={l.id} className="magicedit__row">
                            <span>{l.label_ko}</span>
                            <button
                              type="button"
                              className={on ? "on" : ""}
                              aria-pressed={on}
                              onClick={() =>
                                patchHot(h.id, (x) =>
                                  x.action.kind !== "show"
                                    ? x
                                    : {
                                        ...x,
                                        action: {
                                          ...x.action,
                                          on: on ? x.action.on.filter((i) => i !== l.id) : [...x.action.on, l.id],
                                          off: x.action.off.filter((i) => i !== l.id),
                                        },
                                      },
                                )
                              }
                            >
                              켜기
                            </button>
                            <button
                              type="button"
                              className={off ? "on" : ""}
                              aria-pressed={off}
                              onClick={() =>
                                patchHot(h.id, (x) =>
                                  x.action.kind !== "show"
                                    ? x
                                    : {
                                        ...x,
                                        action: {
                                          ...x.action,
                                          off: off ? x.action.off.filter((i) => i !== l.id) : [...x.action.off, l.id],
                                          on: x.action.on.filter((i) => i !== l.id),
                                        },
                                      },
                                )
                              }
                            >
                              끄기
                            </button>
                          </div>
                        );
                      })}
                      {/* 👨‍👩‍👧 보여주기에도 보호자 문구(2026-10-03) — 26장면 중 12장면이 보여주기뿐이라 넣을 칸이 없었다.
                          누르면 아이 화면에 접힌 「보호자용」 줄이 뜬다(아이 말풍선은 없다) */}
                      <label className="magicedit__guardian">
                        👨‍👩‍👧 보호자에게(선택)
                        <input
                          value={h.action.kind === "show" ? (h.action.guardian_ko ?? "") : ""}
                          maxLength={AR_INTERACT_LIMITS.say}
                          placeholder="「누가 먼저 먹었지?」 하고 물어보세요."
                          onChange={(e) =>
                            patchHot(h.id, (x) =>
                              x.action.kind !== "show" ? x : { ...x, action: { ...x.action, guardian_ko: e.target.value || undefined } },
                            )
                          }
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="magicedit__say">
                      <label>
                        🧒 아이에게
                        <input
                          value={h.action.kind === "say" ? h.action.child_ko : ""}
                          maxLength={AR_INTERACT_LIMITS.say}
                          placeholder="임금님이 쓰던 반짝반짝 금 모자예요."
                          onChange={(e) =>
                            patchHot(h.id, (x) => (x.action.kind !== "say" ? x : { ...x, action: { ...x.action, child_ko: e.target.value } }))
                          }
                        />
                      </label>
                      <label>
                        👨‍👩‍👧 보호자에게(선택)
                        <input
                          value={h.action.kind === "say" ? (h.action.guardian_ko ?? "") : ""}
                          maxLength={AR_INTERACT_LIMITS.say}
                          placeholder="신라 금관. 천마총 출토."
                          onChange={(e) =>
                            patchHot(h.id, (x) =>
                              x.action.kind !== "say" ? x : { ...x, action: { ...x.action, guardian_ko: e.target.value || undefined } },
                            )
                          }
                        />
                      </label>
                      <label>
                        말할 때 앞으로 낼 층(선택)
                        <select
                          value={h.action.kind === "say" ? (h.action.focus ?? "") : ""}
                          onChange={(e) =>
                            patchHot(h.id, (x) =>
                              x.action.kind !== "say" ? x : { ...x, action: { ...x.action, focus: e.target.value || undefined } },
                            )
                          }
                        >
                          <option value="">없음</option>
                          {def.layers.map((l) => (
                            <option key={l.id} value={l.id}>{l.label_ko}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                </div>
              ))}
              {def.layers.length > 0 && def.hotspots.length < AR_INTERACT_LIMITS.hotspots ? (
                <button type="button" className="btn btn--sm" onClick={addHotspot}>
                  ＋ 누르는 자리 더하기
                </button>
              ) : null}
            </>
          ) : null}

          {tab === "slider" ? (
            def.layers.length < 2 ? (
              <p className="hint">층이 두 장 이상이어야 손잡이를 만들 수 있어요(낮 그림·밤 그림처럼).</p>
            ) : (
              <div className="magicedit__slider">
                <label className="bk-check">
                  <input
                    type="checkbox"
                    checked={!!def.slider}
                    onChange={(e) =>
                      save({
                        ...def,
                        slider: e.target.checked
                          ? { label_ko: "낮과 밤", a: def.layers[0]!.id, b: def.layers[1]!.id, iconA: "☀️", iconB: "🌙" }
                          : undefined,
                      })
                    }
                  />
                  손잡이 쓰기 — 끌면 두 그림이 서서히 바뀌어요
                </label>
                {def.slider ? (
                  <>
                    <label>
                      이름
                      <input
                        value={def.slider.label_ko}
                        maxLength={AR_INTERACT_LIMITS.label}
                        onChange={(e) => save({ ...def, slider: { ...def.slider!, label_ko: e.target.value } })}
                      />
                    </label>
                    {(["a", "b"] as const).map((side) => (
                      <div className="magicedit__row" key={side}>
                        <label>
                          {side === "a" ? "왼쪽 끝" : "오른쪽 끝"}
                          <select
                            value={def.slider![side]}
                            onChange={(e) => save({ ...def, slider: { ...def.slider!, [side]: e.target.value } })}
                          >
                            {def.layers.map((l) => (
                              <option key={l.id} value={l.id}>{l.label_ko}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          아이콘
                          <input
                            value={(side === "a" ? def.slider!.iconA : def.slider!.iconB) ?? ""}
                            maxLength={4}
                            aria-label={side === "a" ? "왼쪽 끝 아이콘" : "오른쪽 끝 아이콘"}
                            onChange={(e) =>
                              save({ ...def, slider: { ...def.slider!, [side === "a" ? "iconA" : "iconB"]: e.target.value || undefined } })
                            }
                          />
                        </label>
                      </div>
                    ))}
                  </>
                ) : null}
              </div>
            )
          ) : null}

          {/* 🔴 반만 만든 것은 **저장 전에** 말해 준다 — 판정은 shared(출시 준비와 같은 말). */}
          {issues.length ? (
            <ul className="magicedit__issues">
              {issues.map((s) => (
                <li key={s}>⚠️ {s}</li>
              ))}
            </ul>
          ) : null}
          {def.layers.some((l) => !isArLayerId(l.id)) ? (
            <p className="magicedit__issues">⚠️ 층 이름이 규칙에 안 맞아요(영문 소문자·숫자·_).</p>
          ) : null}
        </div>

        {/* 🔴 미리보기는 **아이 화면과 같은 컴포넌트**다 — 여기서 다시 그리면 갈린다. */}
        <div className="magicedit__preview">
          <div
            ref={stageRef}
            className={`magicedit__stage${picking ? " magicedit__stage--picking" : ""}`}
            style={pageAspect ? { aspectRatio: pageAspect } : undefined}
            onPointerDown={onStagePointerDown}
          >
            {pageImageUrl ? <img src={pageImageUrl} alt="" className="magicedit__page" /> : null}
            {/* 3D 만 올리고 띠가 없어도 눌러 볼 수 있다 — 아이 화면은 3D 를 세운다 */}
            {shown && (Object.keys(layerImgs).length > 0 || Object.keys(layerModels).length > 0) ? (
              <PageMagicPreview def={shown} layers={layerImgs} models={layerModels} />
            ) : (
              <p className="zone__tip">층 그림을 올리면 여기서 눌러 볼 수 있어요.</p>
            )}
            {(tab === "layers" ? [] : def.hotspots).map((h) => (
              <div
                key={h.id}
                className={`magicedit__box${picking?.kind === "hot" && picking.id === h.id ? " on" : ""}`}
                style={{ left: `${h.rect.x * 100}%`, top: `${h.rect.y * 100}%`, width: `${h.rect.w * 100}%`, height: `${h.rect.h * 100}%` }}
              >
                <span>{h.label_ko}</span>
              </div>
            ))}
            {/**
              * 🔴 **층 네모는 ① 층 탭에서 계속 보여 준다.**
              *
              * 처음엔 「자리를 잡는 중일 때만」 그렸다. 그런데 끌기가 끝나면 `picking` 이 풀려서
              * **방금 그린 네모가 사라진다** — 운영자는 어디에 놓았는지 확인하려고 [그림 놓을 자리]를
              * 다시 눌러야 한다. 브라우저로 직접 끌어 보고서야 알았다.
              * 누르는 자리 네모는 늘 보이는데 층만 안 보이면 앞뒤가 안 맞기도 한다.
              * (② 누르는 자리 탭에서는 안 그린다 — 두 색이 겹치면 무엇이 무엇인지 헷갈린다.)
              */}
            {tab === "layers" &&
              def.layers
                .filter((l) => l.rect)
                .map((l) => (
                  <div
                    key={`L${l.id}`}
                    className={`magicedit__box magicedit__box--layer${picking?.kind === "layer" && picking.id === l.id ? " on" : ""}`}
                    style={{ left: `${l.rect!.x * 100}%`, top: `${l.rect!.y * 100}%`, width: `${l.rect!.w * 100}%`, height: `${l.rect!.h * 100}%` }}
                  >
                    <span>🖼️ {l.label_ko}</span>
                  </div>
                ))}
          </div>
          <p className="zone__tip">
            {picking ? "그림 위에서 끌어서 네모를 그려요." : "아이가 보는 그대로예요. 눌러 보세요."}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * 🐘 **이 그림이 아이에게 얼마나 무거운가** — 올린 그림 옆에 한 줄.
 *
 * ## 왜 필요한가 (2026-09-10)
 * 22쪽 팥 꼬투리에 **686KB PNG** 가 올라가 있었다. 교실 회선(1.5Mbps)에서 **3.6초** — 그동안
 * 아이는 빈 칸을 본다. 저장소에는 **픽셀까지 같은 그림**이 209KB WebP 로 있었는데도 몰랐다.
 * 올릴 때 크기를 안 보여 주면 운영자는 **알 방법이 없다** — 화면도 에러도 멀쩡하다.
 *
 * ## 🔴 전에 이 자리에 **거짓 안내**가 있었다
 * 「되돌리는 길은 위 [지우기]가 있다(지우면 저장소 그림이 다시 보인다)」라고 적어 뒀는데,
 * [지우기]는 층을 장면에서 **통째로** 지운다 — 누르는 자리·손잡이까지. 그대로 누르면 놀이가 깨진다.
 * 그래서 **진짜 길**을 붙였다: 저장소에 같은 자리 그림이 있으면 [저장소 그림으로 바꾸기].
 * 한 층씩, 운영자가 골라서만(올린 데는 이유가 있다). 판정은 shared `repoArtOffer`.
 */
/**
 * 🧊 **3D 층 카드** — `.glb` 를 올리면 아이 화면(책 「✨ 마법 보기」·카메라 AR)에 **진짜 3D** 가 선다.
 *
 * ## 무엇이 필요한가
 * · 이 층이 「🖼️ 새 화면」+「🔄 끌면 돌아가요」여야 선다(shared `spinTarget` — 새 화면에 보이는 spin 층)
 * · 돌려 보기 그림(띠)은 **선택** — 3D 를 못 불러오는 폰에서 대신 돌린다. 없으면 「못 불러왔어요」라고 말한다(shared `modelModeLabel`)
 * · 무게: 원본이 **그대로 아이 폰에 간다.** 넘으면 경고·더 넘으면 안 받는다(shared `MODEL_WARN_KB`·`MODEL_MAX_KB`)
 *
 * ## 🔴 전에 이 자리에 **옛 안내**가 있었다 (2026-09-14 고침)
 * 「A 돌려 보는 그림(권장)」·「B 진짜 3D — 준비 중」·「아이는 그림만 받아요」. 실제로는 2026-09-13 부터 아이 화면에 3D 가
 * 서고 있었다. 운영자는 되는 기능을 안 되는 줄 알고, 필요 없는 띠를 만들어 달라고 요청해야 했다.
 *
 * 🔴 `.glb` 는 브라우저가 `File.type` 을 비워 보낸다 — 여기서 `model/gltf-binary` 를 붙여야 엣지가 받는다.
 * 🔴 출처 표기(`credit_ko`)는 남의 자료를 쓸 때 **의무**다(CC BY). 새 화면 아래에 자동으로 붙는다(shared `fullCredits`).
 */
export function Layer3dCard({
  idKey,
  layer,
  model,
  strip,
  setEntry,
  onCredit,
}: {
  idKey: string;
  layer: ArLayerDef;
  model?: ManifestEntry;
  strip?: ManifestEntry;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
  onCredit: (v: string) => void;
}) {
  const key = arLayerModelKey(idKey, layer.id);
  const kb = useKb(model?.url);
  /** 올리고 자리에 적는다 — 무엇을 올릴지(줄일지)는 `ModelFilePicker` 가 정한다. */
  async function upload(file: File) {
    const url = await uploadAsset(key, file); // 관리자에서 여는 주소로 온다(uploadAsset 한 곳이 만진다)
    setEntry(key, { url, alt: file.name });
  }
  /** 3D 가 서는 층인가 — 새 화면 + 끌면 돌아가요(shared `spinTarget` 이 고르는 층의 조건) */
  const stands = !!layer.full && !!layer.spin;
  return (
    <div className="magicedit__3d" data-3d-card>
      <div className="magicedit__row">
        <b>🧊 3D 파일</b>
        {model?.url ? (
          <span className="zone__tip">
            ✓ {model.alt ?? "올라옴"}{" "}
            <button type="button" className="slot__remove" onClick={() => setEntry(key, null)} aria-label={`${layer.label_ko} 3D 파일 지우기`}>
              지우기
            </button>
          </span>
        ) : (
          <ModelFilePicker label={layer.label_ko} onUpload={upload} />
        )}
      </div>
      {/* 🧊 올린 3D 를 그 자리에서 돌려 본다 — 아이 화면과 같은 무대·손짓(누르기 전엔 엔진을 안 싣는다) */}
      {model?.url ? <Model3dMini url={absArtUrl(model.url)} credit={layer.credit_ko} label={layer.label_ko} /> : null}
      {model?.url && kb !== null ? (
        <p className="hint" data-model-kb>
          {kb > MODEL_WARN_KB ? "🐘 " : ""}이 3D 파일 <b>{kb >= 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${Math.round(kb)}KB`}</b> — 교실 회선에서{" "}
          <b>{classLoadSeconds(kb).toFixed(1)}초</b>쯤 걸려요.
        </p>
      ) : null}
      <div className="magicedit__ways" data-3d-ways>
        <p>
          <b>🧊 진짜 3D</b> <span className="zone__tip">(기본)</span> — 올리면 아이 화면(책 「✨ 마법 보기」·카메라 AR)에 바로 서요.
          끌면 돌고, 위아래로 끌면 기울고, 두 손가락으로 벌리면 커져요.
        </p>
        {model?.url && !stands ? (
          <p className="hint" data-3d-needs>
            ⚠️ 이 층의 「🖼️ 새 화면으로 열기」와 「🔄 끌면 돌아가요」를 켜야 3D 가 서요.
          </p>
        ) : null}
        <p>
          <b>🖼️ 대신 그림</b> <span className="zone__tip">(선택)</span> —{" "}
          {strip?.url
            ? "✓ 3D 를 못 띄우는 폰에선 위 「그림」 칸의 돌려 보기 그림을 돌려요."
            : "없어도 돼요. 3D 를 못 불러오면 「3D 를 못 불러왔어요」라고 알려 줘요. 돌려 보기 그림(15도씩 24장)을 위 「그림」 칸에 올리면 그땐 그림으로 돌려요."}
        </p>
        <p className="zone__tip">AI 로 3D 만들기 — 준비 중이에요(3D 생성 API 키가 필요해요).</p>
      </div>
      <label className="bk-field">
        🏷 출처 표기 <span className="zone__tip">(남의 자료를 쓰면 꼭 · 새 화면 아래에 붙어요)</span>
        <input
          value={layer.credit_ko ?? ""}
          maxLength={AR_INTERACT_LIMITS.credit}
          placeholder="예: 국가유산청(KHS) 3D 스캔 · CC BY 4.0"
          aria-label={`${layer.label_ko} 출처 표기`}
          onChange={(e) => onCredit(e.target.value)}
        />
      </label>
    </div>
  );
}

export function LayerArtNote({
  entry,
  repo,
  onUseRepo,
}: {
  entry?: ManifestEntry;
  /** 저장소가 아는 같은 자리(없거나 못 읽었으면 undefined). */
  repo?: ManifestEntry;
  /** [저장소 그림으로 바꾸기] — 초안에만 넣는다(아이에게는 아래 [💾 저장하기] 뒤에 간다). */
  onUseRepo?: (next: ManifestEntry) => void;
}) {
  const kb = useKb(entry?.url);
  const offer = repoArtOffer(entry, repo);
  // 저장소 그림은 상대형(`/art/…`) — 관리자 출처에 붙이면 관리자 index.html 을 재게 된다. 웹 주소로.
  const repoKb = useKb(absArtUrl(offer?.url));
  const [switched, setSwitched] = useState(false);
  if (kb === null && !offer && !switched) return null;
  const heavy = kb !== null && kb > 400; // 1.5Mbps 에서 2초쯤
  return (
    <div className="hint">
      {kb !== null ? (
        <p>
          {heavy ? "🐘 " : ""}
          이 그림 <b>{Math.round(kb)}KB</b>
          {heavy ? (
            <>
              {" "}
              — 교실 회선에서 <b>{classLoadSeconds(kb).toFixed(1)}초</b>쯤 걸려요. 그동안 아이는 빈 칸을 봐요.
              {offer ? null : (
                <>
                  {" "}
                  움직임만 필요하면 <b>띠 대신 한 장 + 위 「움직임」</b>이 훨씬 가벼워요.
                </>
              )}
            </>
          ) : null}
        </p>
      ) : null}
      {offer && onUseRepo ? (
        <p>
          {/* 🔴 「저장소」는 개발자 말이다 — 운영자가 「저장소 그림으로 바꾸기가 뭔지 모르겠다」고 했다(2026-09-11).
              운영자에게는 **무엇이 좋아지는지**로 말한다: 같은 그림인데 가볍다. */}
          📦 이 그림의 <b>가벼운 판</b>이 사이트에 이미 들어 있어요
          {repoKb !== null ? (
            <>
              {" "}
              — <b>{Math.round(repoKb)}KB</b>
              {kb !== null && repoKb < kb ? <> (지금보다 {Math.round((1 - repoKb / kb) * 100)}% 가벼워요)</> : null}
            </>
          ) : null}
          .{" "}
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => {
              onUseRepo(offer);
              setSwitched(true);
            }}
          >
            가벼운 판으로 바꾸기
          </button>
          <br />
          <small>
            다시 올릴 필요 없어요 — 누르면 바로 바뀝니다. 위 미리보기에서 먼저 보시고, 누르는 자리·움직임은 그대로예요.
            {kb !== null && kb > 400 ? " 아이 화면에서 그림이 뜨기까지 빈 칸이 짧아집니다." : ""}
          </small>
        </p>
      ) : null}
      {switched ? (
        <p>
          ↩️ 가벼운 판으로 바꿨어요. 아래 <b>[💾 저장하기]</b>를 눌러야 아이 화면에 가요.
        </p>
      ) : null}
    </div>
  );
}
